import { z } from 'zod'
import { asObjectType } from '@gqloom/zod'

import { type BrandsTableZodSchema } from '../database'

export const STAFF_ALLOWED_NETWORK_LABEL_MAX_LENGTH = 80
export const STAFF_ALLOWED_NETWORK_NOTES_MAX_LENGTH = 500
export const MAX_ALLOWED_NETWORKS_PER_BRAND = 200
export const MAX_ALLOWED_NETWORKS_PER_ASSIGNMENT = 25

export const staffAllowedNetworkSchema = z.object({
  staffAllowedNetworkId: z.uuid(),
  label: z.string().min(1).max(STAFF_ALLOWED_NETWORK_LABEL_MAX_LENGTH),
  cidr: z.string().min(1).max(64),
  ipVersion: z.int(),
  siteId: z.uuid().nullish(),
  notes: z.string().max(STAFF_ALLOWED_NETWORK_NOTES_MAX_LENGTH).nullish(),
  corporateId: z.uuid().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
}) satisfies BrandsTableZodSchema<'StaffAllowedNetwork'>

export const staffAllowedNetworkPublicSchema = staffAllowedNetworkSchema
  .omit({ corporateId: true })
  .register(asObjectType, {
    name: 'StaffAllowedNetwork',
    description:
      'An approved public IP address or CIDR range, with a label, that team members can be restricted to',
  })

const trimmedLabelSchema = z.string().trim().min(1).max(STAFF_ALLOWED_NETWORK_LABEL_MAX_LENGTH)
const trimmedNotesSchema = z.string().trim().max(STAFF_ALLOWED_NETWORK_NOTES_MAX_LENGTH).nullish()

export const staffAllowedNetworkCreateSchema = z.object({
  label: trimmedLabelSchema,
  address: z.string().trim().min(1).max(64),
  siteId: z.uuid().nullish(),
  notes: trimmedNotesSchema,
})

export const staffAllowedNetworkUpdateSchema = staffAllowedNetworkCreateSchema.partial()

export const staffAllowedNetworkIdsSchema = z
  .array(z.uuid())
  .max(MAX_ALLOWED_NETWORKS_PER_ASSIGNMENT)
  .refine((ids) => new Set(ids).size === ids.length, 'Allowed network ids must be unique')

export const staffAllowedNetworksArgsSchema = z.object({
  siteId: z
    .uuid()
    .nullish()
    .describe('Only networks assigned to this location (for location-level team member forms)'),
})

export const staffAllowedNetworkUsageSchema = z
  .object({
    staffUserCount: z.int(),
    staffAccessPolicyCount: z.int(),
  })
  .register(asObjectType, {
    name: 'StaffAllowedNetworkUsage',
    description: 'How many team members and access policies reference an allowed network',
  })

export const clientIpInfoSchema = z
  .object({
    ipAddress: z.string().nullish(),
    isPublic: z.boolean(),
  })
  .register(asObjectType, {
    name: 'ClientIpInfo',
    description: 'The IP address the platform sees for the current request',
  })

export const staffNetworkAccessSchema = z
  .object({
    isExempt: z.boolean().describe('Holds the All Access policy, so is never IP restricted'),
    isRestricted: z.boolean().describe('Can only sign in from the effective allowed networks'),
    effectiveAllowedNetworkIds: z
      .array(z.uuid())
      .describe('Own allowed networks plus those inherited from every assigned access policy'),
    inheritedAllowedNetworkIds: z
      .array(z.uuid())
      .describe('Allowed networks inherited from assigned access policies'),
  })
  .register(asObjectType, {
    name: 'StaffNetworkAccess',
    description: 'Where a team member is allowed to sign in from',
  })

export type StaffAllowedNetworkSchema = z.infer<typeof staffAllowedNetworkSchema>
export type StaffAllowedNetworkPublicSchema = z.infer<typeof staffAllowedNetworkPublicSchema>
export type StaffAllowedNetworkCreateSchema = z.infer<typeof staffAllowedNetworkCreateSchema>
export type StaffAllowedNetworkUpdateSchema = z.infer<typeof staffAllowedNetworkUpdateSchema>
export type StaffAllowedNetworksArgsSchema = z.infer<typeof staffAllowedNetworksArgsSchema>
export type StaffAllowedNetworkUsageSchema = z.infer<typeof staffAllowedNetworkUsageSchema>
export type ClientIpInfoSchema = z.infer<typeof clientIpInfoSchema>
export type StaffNetworkAccessSchema = z.infer<typeof staffNetworkAccessSchema>
